import { useCallback, useEffect, useState } from "react";

import { saveStatsSettings, statsDecks, type StatsDeck } from "@/lib/stats";
import { STATS_TABS, type StatsTab } from "@/lib/navUrl";
import { usePhone } from "@/lib/usePhone";
import { TopBar } from "@/components/shell/TopBar";

import { deckLabels } from "./deckLabels";
import { Coverage } from "./Coverage";
import { History } from "./History";
import { Pace } from "./Pace";
import { Recall } from "./Recall";

/**
 * The four tabs, in the canvas's own words.
 *
 * **The ids stay English** — they are in the URL (`?stats.tab=recall`) and in `navUrl.ts`'s codec,
 * so translating them would break every link anyone has and every check that names one. What is
 * Japanese is what is drawn.
 */
const TAB_LABELS: Record<StatsTab, string> = {
  history: "履歴",
  recall: "定着",
  coverage: "網羅",
  pace: "進度",
};

/**
 * Stats — `8a`–`8d`. Four tabs over one deck (日本-337).
 *
 * Unsplit, capped at `--content-max` and centred past it, like Setup: nothing here rearranges
 * between widths, there is simply more room. Each tab is one request; see `lib/stats.ts`.
 *
 * **The deck picker is shared and scopes everything.** The design draws `Japanese::Vocab ·` as
 * static header text; Alex's call (8 Aug 2026) made it a control over top-level decks, covering the
 * deck and its children with suspended cards excluded. It sits in the top bar rather than in each
 * tab because a per-tab copy is four places the population could differ.
 *
 * **The tab lives in the URL and pushes a history entry; the deck replaces.** History and Pace
 * answer different questions, so Back should return you between them — a deck change is a
 * refinement of the tab you are already on. See `navHistory.classify`.
 */
export function Stats({
  tab,
  deck,
  onTabChange,
  onDeckChange,
  onOpenJob,
}: {
  tab: StatsTab;
  /** `null` until one has been chosen — the server says where to land. */
  deck: string | null;
  onTabChange: (tab: StatsTab) => void;
  onDeckChange: (deck: string) => void;
  /** Calibration is the one panel that can be absent, and its empty state names the job that fills
   *  it. Routed through `App` so it is one history entry rather than a link that reloads. */
  onOpenJob?: () => void;
}) {
  const [decks, setDecks] = useState<StatsDeck[]>([]);
  const [error, setError] = useState<string | null>(null);
  const isPhone = usePhone();

  useEffect(() => {
    let live = true;
    void statsDecks()
      .then((found) => {
        if (!live) return;
        setDecks(found.decks);
        // Only when nothing has been chosen. A deck named in the URL — a link, a refresh — must
        // win over the server's default, or following somebody's link would show your deck.
        if (deck === null && found.default) onDeckChange(found.default);
      })
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
    // Deliberately once: re-running on every deck change would re-ask for a list that only moves
    // when a deck is created, and would fight the choice it had just made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = decks.find((d) => d.name === deck) ?? null;

  /**
   * Remember the deck, server-side.
   *
   * The URL owns it while you are on the surface — a link must show the sender's deck, not yours —
   * but the URL dies with the tab, and the server's fallback is only a guess at what you meant.
   * Fire-and-forget: failing to remember a preference must never interrupt reading the numbers,
   * and the next change will try again.
   */
  const choose = useCallback(
    (name: string) => {
      onDeckChange(name);
      void saveStatsSettings({ deck: name }).catch(() => {});
    },
    [onDeckChange],
  );

  return (
    <>
      {/* The tab strip lives IN the top bar, inline after the surface name, with the scope stated
          right-aligned in grey — measured off the frames. A strip on its own row below cost 45px of
          a 1440px column and read as a second toolbar. */}
      {/* **The phone splits this bar in two, because it cannot hold both halves.** The desktop bar
          is title + four tabs + a card count + a deck picker on one 50px row; at 390px the tabs
          alone want most of it. The Mobile canvas draws the destination and the deck picker on the
          44px top bar and gives the tab strip its own row beneath — which is also what makes the
          strip a full-width scroller rather than a squeezed inline one. */}
      {isPhone ? (
        <>
          <TopBar
            title="統計"
            destinationId="stats"
            phoneActions={<DeckPicker decks={decks} active={deck} onSelect={choose} />}
          />
          <div className="flex h-[40px] shrink-0 items-end border-b border-line px-[16px] box-border">
            <TabStrip active={tab} onSelect={onTabChange} />
          </div>
        </>
      ) : (
      <header className="flex h-[50px] shrink-0 items-center gap-[13px] border-b border-line bg-panel px-[18px] box-border">
        {/* **The title sits in the strip, not beside it.** The tabs are bottom-aligned so their
            underline lands on the bar's own rule, which puts their text ~10px above the baseline a
            vertically-centred title sits on — a misalignment that reads as a rendering fault rather
            than as a choice. Sharing one `items-end` row with the same bottom padding makes the two
            share a baseline by construction instead of by a magic number. */}
        <div className="flex h-full min-w-0 flex-1 items-end gap-[13px]">
          {/* The title carries a tab's own box — same bottom padding, same 2px bottom border (in
              transparent), same `-mb-px` — so the two share a baseline **by construction**. Padding
              alone leaves it a pixel low, because a tab's `-mb-px` drops its box by one. */}
          <span className="-mb-px shrink-0 border-b-2 border-transparent pb-[10px] text-title font-semibold text-ink">
            統計
          </span>
          <TabStrip active={tab} onSelect={onTabChange} />
        </div>
        {active && (
          <span className="hidden shrink-0 font-mono text-[14px]/[14px] text-ink-4 lg:inline">
            {active.cardCount.toLocaleString()}枚
          </span>
        )}
        <DeckPicker decks={decks} active={deck} onSelect={choose} />
      </header>
      )}

      <div className="flex min-h-0 flex-1 flex-col overflow-auto">
        {/* `18px 22px`, the frames' content inset.

            **Stats is the one surface with no `--content-max`, and the handoff says so explicitly**
            — that cap was written for height-bounded surfaces, and Stats is the unsplit surface
            with genuinely long content, so capping it spends width to buy nothing. It is also what
            makes the wide layout reachable at all: capped at 1320 the two-column arrangement below
            would have ~640px a column on a 1920 screen and would be worse than the single column.
            Every panel is fluid, so nothing here has a width to outgrow. */}
        <div className="flex w-full flex-col gap-[14px] px-[22px] py-[18px]">
          {error && (
            <div className="rounded-[var(--r-3)] border border-accent-line bg-accent-tint px-[14px] py-[10px] text-secondary text-ink-2">
              {error}
            </div>
          )}

          {deck === null ? (
            // Not an error and not empty — the deck list is still in flight, and every tab needs a
            // deck before it can ask for anything.
            <p data-check="stats.loading" className="p-[26px] text-secondary text-ink-3">読み込み中…</p>
          ) : (
            <TabBody tab={tab} deck={deck} onOpenJob={onOpenJob} />
          )}
        </div>
      </div>
    </>
  );
}

function TabBody({
  tab,
  deck,
  onOpenJob,
}: {
  tab: StatsTab;
  deck: string;
  onOpenJob?: () => void;
}) {
  switch (tab) {
    case "history":
      return <History deck={deck} />;
    case "coverage":
      return <Coverage deck={deck} />;
    case "recall":
      return <Recall deck={deck} onOpenJob={onOpenJob} />;
    case "pace":
      return <Pace deck={deck} />;
  }
}

function TabStrip({
  active,
  onSelect,
}: {
  active: StatsTab;
  onSelect: (tab: StatsTab) => void;
}) {
  return (
    // `13px/600` and a 2px underline on the active tab, sitting on the top bar's own rule — the
    // frames' treatment. `items-end` is what puts that underline ON the rule rather than floating
    // above it.
    //
    // **It is the flexible element in the bar, and it scrolls.** Four tabs, a title and the deck
    // picker do not fit across 390px, and a top bar that overflows scrolls the PAGE sideways —
    // the app's one standing layout rule, and what `check-stats.mjs` caught at phone width. The
    // `pb-px -mb-px` pair is `Read/TabStrip.tsx`'s and is required for the same reason CLAUDE.md
    // gives: `overflow-x: auto` computes `overflow-y` to `auto` as well, so the active tab's own
    // `-mb-px` would otherwise be clipped and give the strip a pixel of vertical scroll.
    <div
      role="tablist"
      className="flex min-w-0 flex-1 items-end gap-[3px] overflow-x-auto pb-px -mb-px [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {STATS_TABS.map((tab) => (
        <button
          key={tab}
          role="tab"
          aria-selected={active === tab}
          data-check={`stats.tab.${tab}`}
          onClick={() => onSelect(tab)}
          className={`-mb-px shrink-0 border-b-2 px-[8px] pb-[10px] pt-[10px] text-[15px]/[15px] font-semibold transition-colors ${
            active === tab
              ? "border-ink text-ink"
              : "border-transparent text-ink-3 hover:text-ink-2"
          }`}
        >
          {TAB_LABELS[tab]}
        </button>
      ))}
    </div>
  );
}

/**
 * Top-level decks only.
 *
 * Mirrors Cards' segmented control rather than inventing a second deck affordance — the two answer
 * different questions (Cards filters rows, this scopes a statistic) but they are the same gesture,
 * and two shapes for it would be two things to learn.
 */
function DeckPicker({
  decks,
  active,
  onSelect,
}: {
  decks: StatsDeck[];
  active: string | null;
  onSelect: (deck: string) => void;
}) {
  const select = useCallback(
    (name: string) => {
      if (name !== active) onSelect(name);
    },
    [active, onSelect],
  );

  // Leaf names, so four decks fit a 50px bar; full paths only where two leaves collide.
  const labels = deckLabels(decks.map((d) => d.name));

  if (decks.length <= 1) return null;
  return (
    <div className="flex shrink-0 items-center gap-px rounded-[var(--r-2)] border border-line p-px">
      {decks.map((deck) => (
        <button
          key={deck.name}
          onClick={() => select(deck.name)}
          aria-pressed={deck.name === active}
          title={`${deck.name} · ${deck.cardCount.toLocaleString()}枚・停止を除く`}
          className={`rounded-[var(--r-2)] px-[9px] py-[3px] text-meta transition-colors ${
            deck.name === active
              ? "bg-accent-tint font-semibold text-accent-text"
              : "text-ink-3 hover:text-ink-2"
          }`}
        >
          {labels.get(deck.name) ?? deck.name}
        </button>
      ))}
    </div>
  );
}
